// Epoch 35: CSR Users Management Page
// Shows all users from companies assigned to the current CSR
// Date: August 17, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthSession } from '../../hooks/useAuthSession';

interface AssignedUser {
  user_id: string;
  user_email: string;
  full_name: string;
  plan_type: string;
  company_id: string;
  company_name: string;
  campaign_count: number;
  response_count: number;
  last_sign_in_at: string | null;
  created_at: string;
}

type SortField = 'user' | 'company' | 'plan' | 'campaigns' | 'responses' | 'lastActive';
type SortDirection = 'asc' | 'desc';

const CSRUsers: React.FC = () => {
  const [users, setUsers] = useState<AssignedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [sortField, setSortField] = useState<SortField>('user');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const navigate = useNavigate();
  const { session } = useAuthSession();
  const [searchParams] = useSearchParams();
  
  // Get company filter from URL if present
  const companyIdFromUrl = searchParams.get('company');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      // First get companies assigned to this CSR
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('assigned_csr_id', session?.user?.id);

      if (companiesError) throw companiesError;

      if (!companiesData || companiesData.length === 0) {
        setUsers([]);
        setCompanies([]);
        return;
      }

      setCompanies(companiesData);

      // Get all users from those companies
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, full_name, plan_type, company_id, last_sign_in_at, created_at')
        .in('company_id', companiesData.map(c => c.id))
        .eq('role', 'user'); // Only show regular users, not CSRs or admins

      if (usersError) throw usersError;

      if (!usersData || usersData.length === 0) {
        setUsers([]);
        return;
      }

      // Get counts for each user
      const usersWithCounts = await Promise.all(
        usersData.map(async (user) => {
          const company = companiesData.find(c => c.id === user.company_id);
          
          // Count campaigns
          const { count: campaignCount } = await supabase
            .from('campaigns')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

          // Count lead responses
          const { data: campaignIds } = await supabase
            .from('campaigns')
            .select('id')
            .eq('user_id', user.id);

          const { count: responseCount } = await supabase
            .from('lead_responses')
            .select('*', { count: 'exact', head: true })
            .in('campaign_id', campaignIds?.map(c => c.id) || []);

          return {
            user_id: user.id,
            user_email: user.email,
            full_name: user.full_name || 'Not provided',
            plan_type: user.plan_type || 'trial',
            company_id: user.company_id,
            company_name: company?.name || 'Unknown',
            campaign_count: campaignCount || 0,
            response_count: responseCount || 0,
            last_sign_in_at: user.last_sign_in_at,
            created_at: user.created_at
          };
        })
      );

      setUsers(usersWithCounts);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      loadUsers();
    }
  }, [session?.user?.id, loadUsers]);

  useEffect(() => {
    if (companyIdFromUrl) {
      setFilterCompany(companyIdFromUrl);
    }
  }, [companyIdFromUrl]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortUsers = (usersToSort: AssignedUser[]) => {
    return [...usersToSort].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'user': {
          const aUser = a.full_name || a.user_email;
          const bUser = b.full_name || b.user_email;
          compareValue = aUser.localeCompare(bUser);
          break;
        }
        case 'company':
          compareValue = a.company_name.localeCompare(b.company_name);
          break;
        case 'plan':
          compareValue = a.plan_type.localeCompare(b.plan_type);
          break;
        case 'campaigns':
          compareValue = a.campaign_count - b.campaign_count;
          break;
        case 'responses':
          compareValue = a.response_count - b.response_count;
          break;
        case 'lastActive': {
          const aDate = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
          const bDate = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
          compareValue = aDate - bDate;
          break;
        }
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = filterCompany === 'all' || user.company_id === filterCompany;
    return matchesSearch && matchesCompany;
  });

  const sortedUsers = sortUsers(filteredUsers);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      case 'junior_sdr': return 'bg-blue-100 text-blue-800';
      case 'top_qualifier': return 'bg-purple-100 text-purple-800';
      case 'internal': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // EPOCH 63: Renamed "Junior SDR" to "Platform"
  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'trial': return 'Trial';
      case 'junior_sdr': return 'Platform';
      case 'top_qualifier': return 'Top Qualifier';
      case 'internal': return 'Internal';
      default: return plan;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assigned users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Assigned Users</h1>
                <p className="mt-1 text-sm text-gray-600">
                  View and manage users from your assigned companies
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate('/csr-companies')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  View Companies
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="search" className="sr-only">Search users</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="company-filter" className="sr-only">Filter by company</label>
            <select
              id="company-filter"
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
            >
              <option value="all">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{sortedUsers.length}</div>
            <div className="text-sm text-gray-600">Total Users</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {sortedUsers.filter(u => u.plan_type === 'trial').length}
            </div>
            <div className="text-sm text-gray-600">Trial Users</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">
              {sortedUsers.reduce((sum, u) => sum + u.campaign_count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Campaigns</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">
              {sortedUsers.reduce((sum, u) => sum + u.response_count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Responses</div>
          </div>
        </div>

        {/* Users Table */}
        {sortedUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filterCompany !== 'all' ? 'No users found' : 'No users in assigned companies'}
            </h3>
            <p className="text-gray-600">
              {searchTerm || filterCompany !== 'all'
                ? 'Try adjusting your filters'
                : 'Users will appear here once they join your assigned companies'}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('user')}
                  >
                    <div className="flex items-center">
                      User
                      <span className="ml-1">
                        {sortField === 'user' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'user' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center">
                      Company
                      <span className="ml-1">
                        {sortField === 'company' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'company' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('plan')}
                  >
                    <div className="flex items-center">
                      Plan
                      <span className="ml-1">
                        {sortField === 'plan' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'plan' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('campaigns')}
                  >
                    <div className="flex items-center">
                      Campaigns
                      <span className="ml-1">
                        {sortField === 'campaigns' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'campaigns' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('responses')}
                  >
                    <div className="flex items-center">
                      Responses
                      <span className="ml-1">
                        {sortField === 'responses' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'responses' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('lastActive')}
                  >
                    <div className="flex items-center">
                      Last Active
                      <span className="ml-1">
                        {sortField === 'lastActive' && (
                          sortDirection === 'asc' ? '▲' : '▼'
                        )}
                        {sortField !== 'lastActive' && <span className="text-gray-300">⇅</span>}
                      </span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.user_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(user.plan_type)}`}>
                        {getPlanDisplayName(user.plan_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.campaign_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.response_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.last_sign_in_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => navigate(`/campaigns?user=${user.user_id}`)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View Campaigns
                      </button>
                      <a
                        href={`mailto:${user.user_email}`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Contact
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSRUsers;